import './LearningManagement.scss';
import {PageTitle} from "../PageTitle/PageTitle";
import React, {useContext, useMemo, useState, useEffect} from "react";
import {SignDocsListWithFilters} from "./SignDocsListWithFilters/SignDocsListWithFilters";
import {RoutingTabs} from "../../Utils/RoutingTabs/RoutingTabs";
import {FeatureFlags, SignDocCategories} from "../../Utils/Constants";
import {Users} from "../Users/Users";
import {getAllSignDocTypes, SignDocTypes} from "../SignDoc/SignDocLib";
import {I18nReactContext} from "../../contexts/I18nContext";
import {CompanyReactContext} from "../../contexts/CompanyContext";
import {UserReactContext} from "../../contexts/UserContext";
import {MainActionButton} from "../Form/DominantButton/MainActionButton";
import { HoneycombModal } from '../HoneycombTasks/honeycombModal/HoneycombModal';
import { SignDocListRefresherContext } from '../../contexts/SignDocListRefresherContext';
import addGenericIcon from '../../assets/images/honeycomb-icons/general-add-icon.svg';
import {getAdminCreatedTypes} from "../../Utils/TabFiltering/TabFilteringUtils";
import {get, isEmpty} from "lodash";
import {PendingApprovals} from "./PendingApprovalsPage/PendingApprovals";
import {CourseAttendance} from "./CourseAttendancePage/CourseAttendance";
import {useUserAdminOrgs} from "../Hooks/UseUserAdminOrgs";

/**
 * Get all tabs for Learning Management page
 * Now includes consolidated "Tasks" tab instead of individual type tabs
 *
 * @param {object} params - Function parameters
 * @param {object} params.i18n - Internationalization object
 * @param {Array} params.availableTaskTypes - Array of task types (FORCE_COMPLETION) available based on created content and feature flags
 * @param {Array} params.availableCertificationTypes - Array of certification types available based on created content and feature flags
 * @param {Array} params.availableKnowledgeCenterTypes - Array of knowledge center types available based on created content and feature flags
 * @param {Array} params.availableArchiveTypes - Array of types that exist among archived documents
 * @param {object} params.featureFlags - Company feature flags
 * @returns {Array} Array of tab configurations
 */
const getAllTabs = ({i18n, availableTaskTypes = [], availableCertificationTypes = [], availableKnowledgeCenterTypes = [], availableArchiveTypes = [], featureFlags = {}}) => [
    {
        label: i18n.translate('LearningManagementPage.tabs.users'),
        subPath: 'users',
        element: <Users/>,
        alwaysShow: true,
        disabledTooltip: i18n.translate('routingTabs.featureRequiredTooltip', {
            context: { tabName: i18n.translate('LearningManagementPage.tabs.users') }
        })
    },
    {
        label: i18n.translate('LearningManagementPage.tabs.pendingApprovals'),
        subPath: 'pendingApprovals',
        element: <PendingApprovals key={'pendingApprovals'}/>,
        requiredFeatureFlags: [FeatureFlags.UPLOAD_DOCUMENT]
    },
    {
        label: i18n.translate('LearningManagementPage.tabs.courseAttendance'),
        subPath: 'courseAttendance',
        element: <CourseAttendance key={'courseAttendance'}/>,
        requiredFeatureFlags: [FeatureFlags.FRONTAL_COURSE, FeatureFlags.ONLINE_COURSE]
    },
    {
        label: i18n.translate('LearningManagementPage.tabs.tasks'),
        subPath: 'tasks',
        element: <SignDocsListWithFilters
            categories={[SignDocCategories.FORCE_COMPLETION]}
            key={'tasks'}
            types={getAllSignDocTypes()}
            filterableTypes={availableTaskTypes}
            showFilters={{search: true, type: true}}
        />,
        alwaysShow: true,
        disabledTooltip: i18n.translate('routingTabs.featureRequiredTooltip', {
            context: { tabName: i18n.translate('LearningManagementPage.tabs.tasks') }
        })
    },
    {
        label: i18n.translate('LearningManagementPage.tabs.certifications'),
        subPath: 'certifications',
        element: <SignDocsListWithFilters
            categories={[SignDocCategories.CERTIFICATION]}
            key={'certifications'}
            types={getAllSignDocTypes()}
            filterableTypes={availableCertificationTypes}
            showFilters={{search: true, type: true}}
        />,
        requiredFeatureFlags: [FeatureFlags.CERTIFICATION],
        alwaysShow: true,
        disabledTooltip: i18n.translate('routingTabs.featureRequiredTooltip', {
            context: { tabName: i18n.translate('LearningManagementPage.tabs.certifications') }
        })
    },
    {
        label: i18n.translate(featureFlags[FeatureFlags.OPERATION_MODE] ? 'LearningManagementPage.tabs.operation' : 'LearningManagementPage.tabs.knowledgeCenter'),
        subPath: 'adminKnowledgeCenter',
        element: <SignDocsListWithFilters
            categories={[SignDocCategories.KNOWLEDGE_CENTER]}
            key={'adminKnowledgeCenter'}
            types={getAllSignDocTypes()}
            filterableTypes={availableKnowledgeCenterTypes}
            showFilters={{search: true, type: true}}
        />,
        requiredFeatureFlags: [FeatureFlags.KNOWLEDGE_CENTER],
        alwaysShow: true,
        disabledTooltip: i18n.translate('routingTabs.featureRequiredTooltip', {
            context: { tabName: i18n.translate('LearningManagementPage.tabs.knowledgeCenter') }
        })
    },
    {
        label: i18n.translate('LearningManagementPage.tabs.archive'),
        subPath: 'archive',
        element: <SignDocsListWithFilters
            categories={[SignDocCategories.FORCE_COMPLETION, SignDocCategories.CERTIFICATION, SignDocCategories.KNOWLEDGE_CENTER]}
            key={'archive'}
            types={getAllSignDocTypes()}
            filterableTypes={availableArchiveTypes}
            showArchived={true}
            showFilters={{search: true, type: true}}
        />,
        alwaysShow: true
    },
    // { label: i18n.translate('settingsPage.users.title'), subPath: 'users', element: <Box>Users</Box> },

]

export const LearningManagement = () => {
    const {i18n} = useContext(I18nReactContext);
    const companyContext = useContext(CompanyReactContext);
    const userContext = useContext(UserReactContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [adminCreatedTypesForceCompletion, setAdminCreatedTypesForceCompletion] = useState([]);
    const [adminCreatedTypesCertification, setAdminCreatedTypesCertification] = useState([]);
    const [adminCreatedTypesNonForceCompletion, setAdminCreatedTypesNonForceCompletion] = useState([]);
    const [adminCreatedTypesArchive, setAdminCreatedTypesArchive] = useState([]);

    // Check if user has admin organizations
    const { adminOrganizations, isOrgsLoading } = useUserAdminOrgs({ withAllOption: false, withCoursesAndSignDocs: false });

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    // Get refresh trigger from context
    const { refreshTrigger } = useContext(SignDocListRefresherContext);

    // Load admin created types when component mounts or when SignDocs are refreshed
    useEffect(() => {
        const loadAdminCreatedTypes = async () => {
            if (isEmpty(companyContext.featureFlags)) {
                return;
            }
            try {
                // All three calls share one cached createdTypes request —
                // tabs are NOT blocked on this (types only feed filter dropdowns)
                const createdTypesForceCompletion = await getAdminCreatedTypes({
                    categories: [SignDocCategories.FORCE_COMPLETION],
                    isForReportsComponent: false
                });
                setAdminCreatedTypesForceCompletion(createdTypesForceCompletion);

                // Fetch CERTIFICATION types separately
                const createdTypesCertification = await getAdminCreatedTypes({
                    categories: [SignDocCategories.CERTIFICATION],
                    isForReportsComponent: false
                });
                setAdminCreatedTypesCertification(createdTypesCertification);

                // Fetch KNOWLEDGE_CENTER types if feature flag is enabled
                if (companyContext.featureFlags.knowledgeCenterFF) {
                    const createdTypesNonForceCompletion = await getAdminCreatedTypes({
                        categories: [SignDocCategories.KNOWLEDGE_CENTER],
                        isForReportsComponent: false
                    });
                    setAdminCreatedTypesNonForceCompletion(createdTypesNonForceCompletion);
                }

                // Fetch types among ARCHIVED documents for the archive tab dropdown
                // (all categories — the archive tab shows every category)
                const createdTypesArchive = await getAdminCreatedTypes({
                    categories: [],
                    isForReportsComponent: false,
                    showArchived: true
                });
                setAdminCreatedTypesArchive(createdTypesArchive);
            } catch (error) {
                console.error('Error loading admin created types:', error);
                setAdminCreatedTypesForceCompletion([]);
                setAdminCreatedTypesCertification([]);
                setAdminCreatedTypesNonForceCompletion([]);
                setAdminCreatedTypesArchive([]);
            }
        };

        loadAdminCreatedTypes();
    }, [companyContext.featureFlags, refreshTrigger]);

    // Determine if user is company admin or organization admin
    const isCompanyAdmin = userContext.isManagingCompany();
    const hasAdminOrganizations = !isOrgsLoading && adminOrganizations && adminOrganizations.length > 0;
    // Approver-only: user who is approver but NOT company admin and has NO admin organizations
    const isAnyKindOfAdmin = isCompanyAdmin || hasAdminOrganizations;
    const isApproverOnly = !isAnyKindOfAdmin && userContext.isUserApprover();

    /**
     * Calculate available task types for filtering (FORCE_COMPLETION category only)
     */
    const availableTaskTypes = useMemo(() => {
        return [...new Set(adminCreatedTypesForceCompletion)];
    }, [adminCreatedTypesForceCompletion]);

    /**
     * Calculate available certification types for filtering (CERTIFICATION category only)
     */
    const availableCertificationTypes = useMemo(() => {
        return [...new Set(adminCreatedTypesCertification)];
    }, [adminCreatedTypesCertification]);

    /**
     * Calculate available knowledge center types for filtering (KNOWLEDGE_CENTER category only)
     */
    const availableKnowledgeCenterTypes = useMemo(() => {
        return [...new Set(adminCreatedTypesNonForceCompletion)];
    }, [adminCreatedTypesNonForceCompletion]);

    /**
     * Calculate available types among archived documents (archive tab dropdown)
     */
    const availableArchiveTypes = useMemo(() => {
        return [...new Set(adminCreatedTypesArchive)];
    }, [adminCreatedTypesArchive]);

    // Filter tabs based on admin created types and feature flags.
    // NOTE: tabs are intentionally NOT gated on the created-types fetch —
    // created types only feed the type-filter dropdowns, and blocking here
    // used to freeze the whole page behind the heaviest request in the app.
    const filteredTabs = useMemo(() => {
        if (isOrgsLoading) {
            return []; // Show no tabs until admin orgs resolve (fast) — needed for isApproverOnly
        }

        const allTabs = getAllTabs({ i18n, availableTaskTypes, availableCertificationTypes, availableKnowledgeCenterTypes, availableArchiveTypes, featureFlags: companyContext.featureFlags });

        // If user is approver-only (not an admin of any kind), show only tabs they're authorized for
        if (isApproverOnly) {
            return allTabs.filter(tab => {
                // Show Pending Approvals tab if user is authorized to approve documents AND company has uploadDocumentFF
                if (tab.subPath === 'pendingApprovals' &&
                    userContext.isDocumentApprover() &&
                    get(companyContext, 'featureFlags.uploadDocumentFF', false)) {
                    return true;
                }
                // Show Course Attendance tab if user is authorized to approve courses AND company has frontalCourseFF or onlineCourseFF
                if (tab.subPath === 'courseAttendance' &&
                    userContext.isCourseApprover() &&
                    (get(companyContext, 'featureFlags.frontalCourseFF', false) ||
                     get(companyContext, 'featureFlags.onlineCourseFF', false))) {
                    return true;
                }
                return false;
            });
        }

        // For admins, filter tabs based on whether they have created SignDocs
        // Consolidated tab structure: Users, Pending Approvals, Tasks, Certifications, Knowledge Center
        const filtered = allTabs.filter(tab => {
            // Always show Users and Pending Approvals tabs
            if (tab.subPath === 'users' || tab.subPath === 'pendingApprovals') {
                return true;
            }

            // For Tasks tab (FORCE_COMPLETION category)
            if (tab.subPath === 'tasks') {
                const hasTasksContent = adminCreatedTypesForceCompletion.length > 0;
                // Always show Tasks tab if alwaysShow is true OR if there's content
                return tab.alwaysShow || hasTasksContent;
            }

            // For Certifications tab (CERTIFICATION category)
            if (tab.subPath === 'certifications') {
                const hasCertificationsContent = adminCreatedTypesCertification.length > 0;
                const hasFeatureFlag = !tab.requiredFeatureFlags ||
                    tab.requiredFeatureFlags.every(flag => get(companyContext, `featureFlags.${flag}`, false));
                // Always show if alwaysShow is true (will be disabled by RoutingTabs if FF missing),
                // OR show if has feature flag and has content
                return tab.alwaysShow || (hasFeatureFlag && hasCertificationsContent);
            }

            // For Knowledge Center tab
            if (tab.subPath === 'adminKnowledgeCenter') {
                const hasKnowledgeCenterContent = adminCreatedTypesNonForceCompletion.length > 0;
                const hasFeatureFlag = !tab.requiredFeatureFlags ||
                    tab.requiredFeatureFlags.every(flag => get(companyContext, `featureFlags.${flag}`, false));
                // Always show if alwaysShow is true (will be disabled by RoutingTabs if FF missing),
                // OR show if has feature flag and has content
                return tab.alwaysShow || (hasFeatureFlag && hasKnowledgeCenterContent);
            }

            return true;
        });

        return filtered;
    }, [i18n, adminCreatedTypesForceCompletion, adminCreatedTypesCertification, adminCreatedTypesNonForceCompletion, isOrgsLoading, companyContext.featureFlags, isApproverOnly, availableTaskTypes, availableCertificationTypes, availableKnowledgeCenterTypes, availableArchiveTypes]);

    return (
            <div className="LearningManagement MenuPage">
                <PageTitle
                    title={i18n.translate('LearningManagementPage.title')}
                    subTitle={i18n.translate('LearningManagementPage.subTitle')}
                    subTitleBold=""
                    noLogo={false}
                    customComponent={
                        !isApproverOnly ? (
                            <MainActionButton
                                titlePosition={'center'}
                                image={addGenericIcon}
                                imageStyle={{height: 80, marginBottom: 20}}
                                title={i18n.translate('LearningManagementPage.addLearningObject')}
                                onClick={handleOpenModal}
                            />
                        ) : null
                    }
                />
                <div className="menuInnerPage">
                    <div className="learningManagementTabWrapper">
                        <RoutingTabs tabs={filteredTabs} baseRoute={'learningManagement'}/>
                    </div>
                    {isModalOpen && <HoneycombModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    allowedCategories={[
                        SignDocCategories.FORCE_COMPLETION,
                        ...(get(companyContext, 'featureFlags.certificationFF', false) ? [SignDocCategories.CERTIFICATION] : []),
                        ...(get(companyContext, 'featureFlags.knowledgeCenterFF', false) ? [SignDocCategories.KNOWLEDGE_CENTER] : [])
                    ]}
                />}
                </div>
            </div>
    );
}